import * as React from "react";
import {
  fetchProfileAssessmentRows,
  fetchProfileAssessmentEmployeeByAuthUserId,
  getCurrentUser,
  recalculateProfileAssessmentDerivedRows,
  upsertProfileAssessmentRows,
} from "../api/queries";
import { getProfileAssessmentOptions } from "./utils/profileAssessmentOptions";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import { useI18n } from "../i18n/I18nProvider";
import { brand } from "../theme";
import type {
  ProfileAssessmentEmployee,
  ProfileAssessmentStepId,
  ProfileForm,
  StepProps,
} from "../types/assessment";


const ORDER: ProfileAssessmentStepId[] = ["household", "health", "social", "practical"];

const defaultForm: ProfileForm = {
  age_range: "30_44",
  household_size: "1_person",
  living_situation: "alone",
  program_scope: "me_only",

  general_health_capacity: "good",
  prescription_medication_reserve: "no_prescription_dependency",
  mobility_level: "unrestricted",

  response_to_sustained_pressure: "rather_stable",
  housing_type: "multi_family_house",
  children_at_home: "none",
  people_needing_care_count: "0",
  people_needing_care_intensity: "none",
  disability_in_household: "no",
  reliable_contacts_within_30min: "1",
  neighbourhood_support: "some",
  likely_help: "information",

  transport_modes: ["car"],
  need_barrier_free_transport: "no",
  document_copies: "none",
  emergency_docs_at_hand: ["none"],
  practical_skills: ["none"],
  regional_risks: [],
};

function inferAgeRange(age: number | null): ProfileForm["age_range"] {
  if (age === null || Number.isNaN(age)) return "30_44";
  if (age < 18) return "under_18";
  if (age <= 29) return "18_29";
  if (age <= 44) return "30_44";
  if (age <= 64) return "45_64";
  return "65_plus";
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function cleanExclusiveNone(values: string[]) {
  const unique = Array.from(new Set(values));
  if (unique.includes("none") && unique.length > 1) {
    return unique.filter((v) => v !== "none");
  }
  return unique.length ? unique : ["none"];
}

export default function ProfileAssessment(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const setLang: (l: "en" | "de") => void = i18n.setLang ?? (() => {});
  const t: (k: string) => string = i18n.t ?? ((k: string) => k);

  const L = React.useCallback((en: string, de: string) => (lang === "de" ? de : en), [lang]);

  const UI = React.useMemo(
    () => ({
      headerTitle: L("Profile Assessment", "Profil-Assessment"),
      headerSubtitle: L(
        "These answers help personalize your resilience assessment and availability view.",
        "Diese Antworten helfen, dein Resilienz-Assessment und die Verfügbarkeitsansicht zu personalisieren."
      ),
      progressStep: (i: number, total: number) => L(`Step ${i} of ${total}`, `Schritt ${i} von ${total}`),
      btnBack: L("Previous", "Zurück"),
      btnContinue: L("Next", "Weiter"),
      btnSaving: L("Saving...", "Speichert..."),
      btnStartAssessment: L("Continue to assessment", "Weiter zum Assessment"),
      loading: L("Loading profile...", "Profil wird geladen..."),
      errLoad: L("Could not load your profile data.", "Profildaten konnten nicht geladen werden."),
      errSave: L("Could not save your profile data.", "Profildaten konnten nicht gespeichert werden."),
      requiredNote: L(
        "Please answer these profile questions before continuing. They are not scored directly, but they support routing, autofill, and availability insights.",
        "Bitte beantworte diese Profilfragen vor dem Fortfahren. Sie werden nicht direkt bewertet, unterstützen aber Routing, Autofill und Verfügbarkeitsauswertungen."
      ),
      availabilityNote: L(
        "Availability-relevant fields include children, care duties, transport access and emergency-volunteer status from EKS.",
        "Verfügbarkeitsrelevante Felder umfassen Kinder, Pflegepflichten, Transportzugang und Ehrenamtsstatus aus EKS."
      ),

      householdTitle: L("Household context", "Haushaltskontext"),
      householdBody: L(
        "These answers decide whether household-related questions and recommendations are relevant.",
        "Diese Antworten bestimmen, ob haushaltsbezogene Fragen und Empfehlungen relevant sind."
      ),
      ageRange: L("Age range", "Altersgruppe"),
      householdSize: L("Household size", "Haushaltsgröße"),
      livingSituation: L("Living situation", "Wohnsituation"),
      programScope: L("Program scope", "Programmumfang"),

      healthTitle: L("Health and mobility", "Gesundheit und Mobilität"),
      healthBody: L(
        "These answers support physical capacity, medication reserve and evacuation planning logic.",
        "Diese Antworten unterstützen Logik zu körperlicher Belastbarkeit, Medikamentenreserve und Evakuierungsplanung."
      ),
      generalHealth: L("General health and physical capacity", "Allgemeine Gesundheit und körperliche Leistungsfähigkeit"),
      medicationReserve: L("Prescription medication reserve", "Vorrat an verschreibungspflichtigen Medikamenten"),
      mobility: L("Mobility level", "Mobilitätsniveau"),

      socialTitle: L("Care, stress and support network", "Betreuung, Stress und Unterstützungsnetzwerk"),
      socialBody: L(
        "These answers support personal vulnerability, household planning and manager availability insights.",
        "Diese Antworten unterstützen persönliche Verwundbarkeit, Haushaltsplanung und Verfügbarkeitsauswertungen für Manager."
      ),
      pressure: L("Response under sustained pressure", "Reaktion unter anhaltendem Druck"),
      housingType: L("Housing type", "Wohnform"),
      children: L("Children at home", "Kinder im Haushalt"),
      careCount: L("People needing care in household", "Pflegebedürftige Personen im Haushalt"),
      careIntensity: L("Care intensity", "Pflegeintensität"),
      disability: L("Disability or special support need in household", "Behinderung oder besonderer Unterstützungsbedarf im Haushalt"),
      contacts: L("Reliable contacts within 30 minutes", "Verlässliche Kontakte innerhalb von 30 Minuten"),
      neighbourhood: L("Neighbourhood support", "Nachbarschaftliche Unterstützung"),
      likelyHelp: L("Most likely help available", "Wahrscheinlichste Hilfe"),

      practicalTitle: L("Practical capability", "Praktische Fähigkeiten"),
      practicalBody: L(
        "These answers support evacuation, document readiness, practical preparedness and module routing.",
        "Diese Antworten unterstützen Evakuierung, Dokumentenbereitschaft, praktische Vorbereitung und Modul-Routing."
      ),
      transport: L("Transport modes available", "Verfügbare Transportmöglichkeiten"),
      barrierFree: L("Need barrier-free transport?", "Barrierefreier Transport erforderlich?"),
      docsCopies: L("Document copies", "Dokumentenkopien"),
      emergencyDocs: L("Emergency documents at hand", "Notfalldokumente griffbereit"),
      skills: L("Practical skills", "Praktische Fähigkeiten"),
      risks: L("Regional risks where you live", "Regionale Risiken an deinem Wohnort"),
    }),
    [L]
  );

  const options = React.useMemo(() => getProfileAssessmentOptions(L), [L]);
  const [employee, setEmployee] = React.useState<ProfileAssessmentEmployee | null>(null);
  const [form, setForm] = React.useState<ProfileForm>(defaultForm);
  const [idx, setIdx] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr || !userData?.user) throw new Error("Not authenticated.");

        const { data: empRow, error: empErr } =
          await fetchProfileAssessmentEmployeeByAuthUserId(userData.user.id);

        if (empErr) throw empErr;
        if (!empRow) throw new Error("Employee record not found.");

        const employeeId = empRow.id as string;

        const [identityRes, healthRes, socialRes, practicalRes] =
          await fetchProfileAssessmentRows(employeeId);

        if (identityRes.error) throw identityRes.error;
        if (healthRes.error) throw healthRes.error;
        if (socialRes.error) throw socialRes.error;
        if (practicalRes.error) throw practicalRes.error;

        const next: ProfileForm = {
          ...defaultForm,
          age_range: identityRes.data?.age_range ?? inferAgeRange(empRow.age ?? null),
          household_size: identityRes.data?.household_size ?? defaultForm.household_size,
          living_situation: identityRes.data?.living_situation ?? defaultForm.living_situation,
          program_scope: identityRes.data?.program_scope ?? defaultForm.program_scope,

          general_health_capacity: healthRes.data?.general_health_capacity ?? defaultForm.general_health_capacity,
          prescription_medication_reserve:
            healthRes.data?.prescription_medication_reserve ?? defaultForm.prescription_medication_reserve,
          mobility_level: healthRes.data?.mobility_level ?? defaultForm.mobility_level,

          response_to_sustained_pressure:
            socialRes.data?.response_to_sustained_pressure ?? defaultForm.response_to_sustained_pressure,
          housing_type: socialRes.data?.housing_type ?? defaultForm.housing_type,
          children_at_home: socialRes.data?.children_at_home ?? defaultForm.children_at_home,
          people_needing_care_count:
            socialRes.data?.people_needing_care_count ?? defaultForm.people_needing_care_count,
          people_needing_care_intensity:
            socialRes.data?.people_needing_care_intensity ?? defaultForm.people_needing_care_intensity,
          disability_in_household: socialRes.data?.disability_in_household ?? defaultForm.disability_in_household,
          reliable_contacts_within_30min:
            socialRes.data?.reliable_contacts_within_30min ?? defaultForm.reliable_contacts_within_30min,
          neighbourhood_support: socialRes.data?.neighbourhood_support ?? defaultForm.neighbourhood_support,
          likely_help: socialRes.data?.likely_help ?? defaultForm.likely_help,

          transport_modes: practicalRes.data?.transport_modes ?? defaultForm.transport_modes,
          need_barrier_free_transport:
            practicalRes.data?.need_barrier_free_transport ?? defaultForm.need_barrier_free_transport,
          document_copies: practicalRes.data?.document_copies ?? defaultForm.document_copies,
          emergency_docs_at_hand:
            practicalRes.data?.emergency_docs_at_hand ?? defaultForm.emergency_docs_at_hand,
          practical_skills: practicalRes.data?.practical_skills ?? defaultForm.practical_skills,
          regional_risks: practicalRes.data?.regional_risks ?? defaultForm.regional_risks,
        };

        if (!mounted) return;
        setEmployee({ id: employeeId, age: empRow.age ?? null });
        setForm(next);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? UI.errLoad);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, [UI.errLoad]);

  const step = ORDER[idx];
  const progress = clamp(((idx + 1) / ORDER.length) * 100, 0, 100);

  const setField = <K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) => {
    setForm((old) => ({ ...old, [key]: value }));
  };

  const saveProfile = async () => {
    if (!employee) return;

    setSaving(true);
    setError(null);

    try {
      const now = new Date().toISOString();
      const employee_id = employee.id;

      const identityPayload = {
        employee_id,
        age_range: form.age_range,
        household_size: form.household_size,
        living_situation: form.living_situation,
        program_scope: form.program_scope,
        updated_at: now,
      };

      const healthPayload = {
        employee_id,
        general_health_capacity: form.general_health_capacity,
        prescription_medication_reserve: form.prescription_medication_reserve,
        mobility_level: form.mobility_level,
        updated_at: now,
      };

      const socialPayload = {
        employee_id,
        response_to_sustained_pressure: form.response_to_sustained_pressure,
        housing_type: form.housing_type,
        children_at_home: form.children_at_home,
        people_needing_care_count: form.people_needing_care_count,
        people_needing_care_intensity: form.people_needing_care_intensity,
        disability_in_household: form.disability_in_household,
        reliable_contacts_within_30min: form.reliable_contacts_within_30min,
        neighbourhood_support: form.neighbourhood_support,
        likely_help: form.likely_help,
        updated_at: now,
      };

      const practicalPayload = {
        employee_id,
        transport_modes: cleanExclusiveNone(form.transport_modes),
        need_barrier_free_transport: form.need_barrier_free_transport,
        document_copies: form.document_copies,
        emergency_docs_at_hand: cleanExclusiveNone(form.emergency_docs_at_hand),
        practical_skills: cleanExclusiveNone(form.practical_skills),
        regional_risks: form.regional_risks,
        updated_at: now,
      };

      const results = await upsertProfileAssessmentRows({
        identityPayload,
        healthPayload,
        socialPayload,
        practicalPayload,
      });

      const firstError = results.find((r) => r.error)?.error;
      if (firstError) throw firstError;

      await recalculateProfileAssessmentDerivedRows(employee_id);
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (idx < ORDER.length - 1) {
      setIdx((old) => old + 1);
      return;
    }

    try {
      await saveProfile();
      handlePageChange("PBGtest");
    } catch (e: any) {
      setError(e?.message ?? UI.errSave);
    }
  };

  const goBack = () => {
    if (idx === 0) {
      handlePageChange("OccasionSelection");
      return;
    }
    setIdx((old) => Math.max(old - 1, 0));
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6", p: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={iconBoxSx}>
              <GroupsOutlinedIcon />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827", lineHeight: 1.1 }}>
                {UI.headerTitle}
              </Typography>
              <Typography variant="body2" sx={{ color: "#6B7280" }}>
                {UI.headerSubtitle}
              </Typography>
            </Box>
          </Stack>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={lang}
            onChange={(_, next) => {
              if (next) setLang(next);
            }}
            sx={{
              bgcolor: "#FFFFFFB3",
              border: "1px solid #00000014",
              borderRadius: 999,
              overflow: "hidden",
              "& .MuiToggleButton-root": {
                px: 1.1,
                py: 0.45,
                fontWeight: 900,
                textTransform: "none",
                border: 0,
                color: "#111827B3",
              },
              "& .Mui-selected": {
                color: "#111827",
                bgcolor: "#11182712",
              },
            }}
          >
            <ToggleButton value="en">{t("lang.en")}</ToggleButton>
            <ToggleButton value="de">{t("lang.de")}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", overflow: "hidden" }}>
          <Box sx={{ p: 2 }}>
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
                <Typography sx={{ fontWeight: 900, color: "#111827" }}>{UI.headerTitle}</Typography>
                <Typography variant="body2" sx={{ color: "#6B7280", fontWeight: 800 }}>
                  {UI.progressStep(idx + 1, ORDER.length)}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 10,
                  borderRadius: 20,
                  bgcolor: brand.border,
                  "& .MuiLinearProgress-bar": { borderRadius: 20, bgcolor: brand.accent },
                }}
              />
            </Stack>
          </Box>

          <Divider />

          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "360px 1fr" }, gap: 2.5 }}>
              <Stack spacing={2}>
                <SideNote title={UI.headerTitle} body={UI.requiredNote} />
                <SideNote title={L("Your availability", "Dein Verfügbarkeit")} body={UI.availabilityNote} muted />
              </Stack>

              <Stack spacing={2}>
                <Box sx={{ borderRadius: 3, border: "1px solid #E5E7EB", bgcolor: "#FFFFFF", p: 2.25 }}>
                  {loading ? (
                    <Typography sx={{ fontWeight: 900, color: "#111827" }}>{UI.loading}</Typography>
                  ) : step === "household" ? (
                    <HouseholdStep UI={UI} options={options} form={form} setField={setField} />
                  ) : step === "health" ? (
                    <HealthStep UI={UI} options={options} form={form} setField={setField} />
                  ) : step === "social" ? (
                    <SocialStep UI={UI} options={options} form={form} setField={setField} />
                  ) : (
                    <PracticalStep UI={UI} options={options} form={form} setField={setField} />
                  )}
                </Box>

                {error && (
                  <Typography variant="body2" sx={{ color: "#B91C1C", fontWeight: 800 }}>
                    {error}
                  </Typography>
                )}

                <Stack direction="row" spacing={1.25} justifyContent="space-between">
                  <Button
                    variant="outlined"
                    onClick={goBack}
                    disabled={saving || loading}
                    startIcon={<ArrowBackIcon />}
                    sx={btnOutlineSx}
                  >
                    {UI.btnBack}
                  </Button>

                  <Button
                    variant="contained"
                    onClick={goNext}
                    disabled={saving || loading || !employee}
                    endIcon={<ArrowForwardIcon />}
                    sx={btnPrimarySx}
                  >
                    {saving ? UI.btnSaving : idx === ORDER.length - 1 ? UI.btnStartAssessment : UI.btnContinue}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

function HouseholdStep(props: StepProps) {
  const { UI, options, form, setField } = props;
  return (
    <StepShell icon={<HomeOutlinedIcon />} title={UI.householdTitle} body={UI.householdBody}>
      <SelectField label={UI.ageRange} value={form.age_range} options={options.ageRange} onChange={(v) => setField("age_range", v as any)} />
      <SelectField label={UI.householdSize} value={form.household_size} options={options.householdSize} onChange={(v) => setField("household_size", v as any)} />
      <SelectField label={UI.livingSituation} value={form.living_situation} options={options.livingSituation} onChange={(v) => setField("living_situation", v as any)} />
      <SelectField label={UI.programScope} value={form.program_scope} options={options.programScope} onChange={(v) => setField("program_scope", v as any)} />
    </StepShell>
  );
}

function HealthStep(props: StepProps) {
  const { UI, options, form, setField } = props;
  return (
    <StepShell icon={<HealthAndSafetyOutlinedIcon />} title={UI.healthTitle} body={UI.healthBody}>
      <SelectField label={UI.generalHealth} value={form.general_health_capacity} options={options.generalHealth} onChange={(v) => setField("general_health_capacity", v as any)} />
      <SelectField label={UI.medicationReserve} value={form.prescription_medication_reserve} options={options.medicationReserve} onChange={(v) => setField("prescription_medication_reserve", v as any)} />
      <SelectField label={UI.mobility} value={form.mobility_level} options={options.mobility} onChange={(v) => setField("mobility_level", v as any)} />
    </StepShell>
  );
}

function SocialStep(props: StepProps) {
  const { UI, options, form, setField } = props;
  return (
    <StepShell icon={<GroupsOutlinedIcon />} title={UI.socialTitle} body={UI.socialBody}>
      <SelectField label={UI.pressure} value={form.response_to_sustained_pressure} options={options.pressure} onChange={(v) => setField("response_to_sustained_pressure", v as any)} />
      <SelectField label={UI.housingType} value={form.housing_type} options={options.housingType} onChange={(v) => setField("housing_type", v as any)} />
      <SelectField label={UI.children} value={form.children_at_home} options={options.children} onChange={(v) => setField("children_at_home", v as any)} />
      <SelectField label={UI.careCount} value={form.people_needing_care_count} options={options.careCount} onChange={(v) => setField("people_needing_care_count", v as any)} />
      <SelectField label={UI.careIntensity} value={form.people_needing_care_intensity} options={options.careIntensity} onChange={(v) => setField("people_needing_care_intensity", v as any)} />
      <SelectField label={UI.disability} value={form.disability_in_household} options={options.disability} onChange={(v) => setField("disability_in_household", v as any)} />
      <SelectField label={UI.contacts} value={form.reliable_contacts_within_30min} options={options.contacts} onChange={(v) => setField("reliable_contacts_within_30min", v as any)} />
      <SelectField label={UI.neighbourhood} value={form.neighbourhood_support} options={options.neighbourhood} onChange={(v) => setField("neighbourhood_support", v as any)} />
      <SelectField label={UI.likelyHelp} value={form.likely_help} options={options.likelyHelp} onChange={(v) => setField("likely_help", v as any)} />
    </StepShell>
  );
}

function PracticalStep(props: StepProps) {
  const { UI, options, form, setField } = props;
  return (
    <StepShell icon={<BuildOutlinedIcon />} title={UI.practicalTitle} body={UI.practicalBody}>
      <MultiSelectField label={UI.transport} value={form.transport_modes} options={options.transportModes} onChange={(v) => setField("transport_modes", cleanExclusiveNone(v) as any)} />
      <SelectField label={UI.barrierFree} value={form.need_barrier_free_transport} options={options.barrierFree} onChange={(v) => setField("need_barrier_free_transport", v as any)} />
      <SelectField label={UI.docsCopies} value={form.document_copies} options={options.docsCopies} onChange={(v) => setField("document_copies", v as any)} />
      <MultiSelectField label={UI.emergencyDocs} value={form.emergency_docs_at_hand} options={options.emergencyDocs} onChange={(v) => setField("emergency_docs_at_hand", cleanExclusiveNone(v) as any)} />
      <MultiSelectField label={UI.skills} value={form.practical_skills} options={options.skills} onChange={(v) => setField("practical_skills", cleanExclusiveNone(v) as any)} />
      <MultiSelectField label={UI.risks} value={form.regional_risks} options={options.risks} onChange={(v) => setField("regional_risks", v as any)} allowEmpty />
    </StepShell>
  );
}

function StepShell(props: { icon: React.ReactNode; title: string; body: string; children: React.ReactNode }) {
  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
        <Box sx={{ ...iconBoxSx, width: 40, height: 40 }}>{props.icon}</Box>
        <Box>
          <Typography sx={{ fontWeight: 900, fontSize: 28, color: "#111827", lineHeight: 1.15 }}>
            {props.title}
          </Typography>
        </Box>
      </Stack>

      <Typography variant="body1" sx={{ color: "#6B7280", mb: 2.25, lineHeight: 1.7 }}>
        {props.body}
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {props.children}
      </Box>
    </Box>
  );
}

function SelectField(props: {
  label: string;
  value: string;
  options: string[][];
  onChange: (value: string) => void;
}) {
  return (
    <TextField
      fullWidth
      select
      label={props.label}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
    >
      {props.options.map(([value, label]) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
    </TextField>
  );
}

function MultiSelectField(props: {
  label: string;
  value: string[];
  options: string[][];
  onChange: (value: string[]) => void;
  allowEmpty?: boolean;
}) {
  const labelByValue = React.useMemo(() => Object.fromEntries(props.options), [props.options]);

  return (
    <FormControl fullWidth>
      <InputLabel>{props.label}</InputLabel>
      <Select
        multiple
        value={props.value}
        input={<OutlinedInput label={props.label} />}
        onChange={(e) => {
          const value = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
          props.onChange(props.allowEmpty ? Array.from(new Set(value)) : cleanExclusiveNone(value));
        }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.length === 0 ? (
              <Typography variant="body2" sx={{ color: "#9CA3AF" }}>—</Typography>
            ) : (
              selected.map((value) => <Chip key={value} label={labelByValue[value] ?? value} size="small" />)
            )}
          </Box>
        )}
      >
        {props.options.map(([value, label]) => (
          <MenuItem key={value} value={value}>
            <Checkbox checked={props.value.includes(value)} />
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function SideNote(props: { title: string; body: string; muted?: boolean }) {
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        bgcolor: props.muted ? "#F9FAFB" : "#FFFFFF",
        p: 2,
      }}
    >
      <Typography sx={{ fontWeight: 900, color: "#111827" }}>{props.title}</Typography>
      <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.5, lineHeight: 1.6 }}>
        {props.body}
      </Typography>
    </Box>
  );
}

const iconBoxSx = {
  width: 44,
  height: 44,
  borderRadius: 2.5,
  bgcolor: "#FFFFFF",
  border: "1px solid #E5E7EB",
  display: "grid",
  placeItems: "center",
  color: "#111827",
  flexShrink: 0,
} as const;

const btnPrimarySx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  bgcolor: "#111827",
  boxShadow: "none",
  px: 2.2,
  py: 1.1,
  "&:hover": { bgcolor: "#0B1220", boxShadow: "none" },
} as const;

const btnOutlineSx = {
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "#D1D5DB",
  color: "#111827",
  bgcolor: "#FFFFFF",
  px: 2.2,
  py: 1.1,
  "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
} as const;
